import React, {Component} from "react";
import {DataGridContainer} from "app/modules/common/grid/data-grid-container";
import {DataGridHeader} from "app/modules/common/grid/data-grid-header";
import {Button, Chip, Grid, MenuItem, Select} from "@mui/material";
import {DataGridBody} from "app/modules/common/grid/data-grid-body";
import {DataGridPro, GridRenderCellParams} from "@mui/x-data-grid-pro";
import {inject, provider} from "react-ioc";
import {observer} from "mobx-react";
import {Link, useParams} from "react-router-dom";
import {CompanyIntegrationLogsStore} from "app/modules/company/components/company-integrations/logs/CompanyIntegrationLogsStore";
import {PageTitleChip} from "app/modules/common/layout/components/page-title";
import {BoxCenter} from "app/modules/common/layout/components/containers";
import {DateTimeHelper} from "app/modules/common/helpers/DateTimeHelper";
import {Loading} from "app/modules/common/layout/components/loading";
import {Paths} from "app/modules/company/stores/paths";
import {
    CompanyIntegrationLogListModel,
    CompanyIntegrationLogType,
} from "app/modules/company/api/clients.api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const typeLabel = (t: CompanyIntegrationLogType) => {
    switch (t) {
        case CompanyIntegrationLogType.Error: return "Error";
        case CompanyIntegrationLogType.Info: return "Info";
        case CompanyIntegrationLogType.Warning: return "Warning";
        default: return "";
    }
};

const typeColor = (t: CompanyIntegrationLogType): "error" | "info" | "warning" | "default" => {
    switch (t) {
        case CompanyIntegrationLogType.Error: return "error";
        case CompanyIntegrationLogType.Info: return "info";
        case CompanyIntegrationLogType.Warning: return "warning";
        default: return "default";
    }
};

const columns = [
    {
        field: "log",
        headerName: "Log",
        minWidth: 300,
        flex: 2,
    },
    {
        field: "type",
        headerName: "Type",
        minWidth: 120,
        flex: 0,
        renderCell: (params: GridRenderCellParams<CompanyIntegrationLogListModel>) =>
            params.row.type != null ? (
                <Chip size="small" label={typeLabel(params.row.type)} color={typeColor(params.row.type)} />
            ) : (
                ""
            ),
    },
    {
        field: "createdAt",
        headerName: "Created",
        minWidth: 180,
        flex: 1,
        renderCell: (params: GridRenderCellParams<CompanyIntegrationLogListModel>) =>
            params.row.createdAt ? DateTimeHelper.formatMomentDateTimeUtcToLocal(params.row.createdAt) : "",
    },
];

@provider(CompanyIntegrationLogsStore)
@observer
class Index extends Component<any, any> {
    @inject logsStore: CompanyIntegrationLogsStore;

    render() {
        if (this.logsStore.isInitLoading) {
            return <Loading />;
        }

        return (
            <DataGridContainer>
                <Grid item xs={6}>
                    <PageTitleChip
                        Title={this.logsStore.integrationName ? `Logs: ${this.logsStore.integrationName}` : "Integration Logs"}
                        TotalRows={this.logsStore.totalRows}
                    />
                </Grid>
                <Grid item xs={6} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                    <Button component={Link} to={Paths.EditCompanyIntegration.nav(this.logsStore.integrationId)} startIcon={<ArrowBackIcon />} size="small">
                        Back to integration
                    </Button>
                </Grid>
                <DataGridHeader>
                    <BoxCenter>
                        <Select
                            size="small"
                            value={this.logsStore.filters.$.type.value === "" ? "all" : this.logsStore.filters.$.type.value}
                            onChange={(e) => {
                                const v = e.target.value;
                                this.logsStore.filters.$.type.onChange(v === "all" ? "" : (v as number));
                                this.logsStore.applyFilters();
                            }}
                            sx={{ minWidth: 140 }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value={CompanyIntegrationLogType.Error}>Error</MenuItem>
                            <MenuItem value={CompanyIntegrationLogType.Info}>Info</MenuItem>
                            <MenuItem value={CompanyIntegrationLogType.Warning}>Warning</MenuItem>
                        </Select>
                        <Button variant="text" size="small" onClick={() => this.logsStore.applyFilters()} sx={{ ml: 1 }}>
                            Apply
                        </Button>
                    </BoxCenter>
                </DataGridHeader>
                <DataGridBody>
                    <DataGridPro
                        getRowId={(row) => row.id ?? 0}
                        disableVirtualization
                        rows={this.logsStore.items}
                        columns={columns}
                        pageSize={this.logsStore.pageSize}
                        rowsPerPageOptions={this.logsStore.pageSizeOptions}
                        rowCount={this.logsStore.totalRows}
                        page={this.logsStore.skipRows / this.logsStore.pageSize}
                        loading={this.logsStore.isLoading}
                        pagination
                        paginationMode="server"
                        disableColumnFilter
                        disableColumnSelector
                        disableSelectionOnClick
                        checkboxSelection={false}
                        onPageChange={(page) => this.logsStore.setPage(page)}
                        onPageSizeChange={(pageSize) => this.logsStore.setPageSize(pageSize)}
                    />
                </DataGridBody>
            </DataGridContainer>
        );
    }

    componentDidMount() {
        const id = this.props.urlParams?.id;
        const n = typeof id === "string" ? parseInt(id, 10) : id;
        if (!isNaN(n)) this.logsStore.init(n);
    }
}

export default function CompanyIntegrationLogsIndex(props: any) {
    const urlParams = useParams<{ id: string }>();
    return <Index {...props} urlParams={urlParams} key={urlParams.id} />;
}
